/**
 * Coquille applicative — barre latérale groupée + top bar + contenu.
 *
 * Remplace la barre de navigation plate. Conserve les contrats d'accessibilité : lien
 * d'évitement en premier élément focusable, landmark de navigation nommé (dans
 * AppSidebar), zone `#main` focusable.
 */
import { useCallback, useEffect, useRef, useState, type JSX, type ReactNode } from 'react'

import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar'
import { Toaster } from '@/components/ui/sonner'
import { AppSidebar } from '@/components/AppSidebar'
import { TopBar } from '@/components/TopBar'
import { ProchaineActionProjet } from '@/components/ProchaineActionProjet'
import { FilAriane } from '@/components/FilAriane'
import { BandeTelephone } from '@/components/BandeTelephone'
import { RailTablette } from '@/components/RailTablette'
import { FeuilleContexte } from '@/components/contexte/FeuilleContexte'
import { useVarianteCoquille } from '@/hooks/useVarianteCoquille'
import { applyTheme, getTheme } from '@/lib/theme'
import { CommandPalette } from '@/components/CommandPalette'
import { WorkflowRail } from '@/components/WorkflowRail'
import { NewWorkspaceDialog } from '@/components/NewWorkspaceDialog'
import { ConfirmationProvider } from '@/components/ConfirmationProvider'
import { RegionVivanteProvider } from '@/components/RegionVivante'
import type { Session } from '@/auth/session'
import { useT } from '@/i18n/language'
import type { NavigationMode } from '@/lib/navigationMode'
import { useNavigationMode } from '@/hooks/useNavigationMode'
import { useEffectiveNavRoles } from '@/hooks/useEffectiveNavRoles'
import { useWorkspace } from '@/hooks/useWorkspace'
import { BarreContexte } from '@/components/contexte/BarreContexte'
import { BandeauArretIA } from '@/components/arret/BandeauArretIA'
import { BandeauAccesExceptionnel } from '@/components/plateforme/BandeauAccesExceptionnel'
import { BandeauCycleDeVie } from '@/components/cycle/BandeauCycleDeVie'
import { estAdministrationOrganisation } from '@/api/cycleDeVieApi'
import { AvisDemenagement } from '@/components/Demenagement'
import { useNiveau } from '@/hooks/useNiveau'
import { FournisseurPageInconnue } from '@/hooks/usePageInconnue'
import { PorteProjet } from '@/components/PorteProjet'
import { QuotaBadgeSlotPied } from '@/components/quota/QuotaBadgeSlotPied'
import { RegistreSelonProfil } from '@/components/profil/RegistreSelonProfil'

export function AppShell({ session, children }: {
  session: Session
  children: ReactNode
}): JSX.Element {
  const skipLinkRef = useRef<HTMLAnchorElement>(null)
  const [commandOpen, setCommandOpen] = useState(false)
  // Dialogue « Nouveau projet » monté une seule fois ici (contrôlé) : ouvert indifféremment
  // par le menu « Créer » de la top bar ou par la palette ⌘K, sans imbrication de dialogues.
  const [newProjectOpen, setNewProjectOpen] = useState(false)
  const { t } = useT()
  // Rôle EFFECTIF de l'espace ouvert (v030-po-ux-01), pas le rôle global du jeton : le rail
  // et la palette doivent montrer exactement ce que `WorkspaceMembershipGuard` applique
  // réellement pour CET espace, jamais le rôle réalm de session seul.
  const { activeId: workspaceIdActif } = useWorkspace()
  // La préférence est persistée PAR WORKSPACE depuis v030-po-auth-02 : `workspaceIdActif`
  // doit donc être connu AVANT d'appeler ce hook (déplacé après useWorkspace()), sans
  // quoi un changement d'espace ne changerait jamais la portée du cache.
  const {
    mode: navigationMode, setMode: setNavigationMode, error: navigationModeError,
    conflict: navigationModeConflict, confirmFreshValue: confirmNavigationModeFreshValue,
  } = useNavigationMode(session, workspaceIdActif ?? undefined)
  const rolesEffectifs = useEffectiveNavRoles(session, workspaceIdActif)
  const sessionPourNav: Session = { ...session, roles: rolesEffectifs }
  const openNewProject = (): void => setNewProjectOpen(true)
  // U6b vague 1 : quatre niveaux (Plateforme, Organisation, Programme, Projet), chacun sa barre
  // latérale ; la route décide, une route transverse garde le niveau courant.
  const niveauDeLaRoute = useNiveau({ espaceOuvert: Boolean(workspaceIdActif), superAdmin: session.roles.includes('admin') })
  // Lot NAV 0.5.x (A2 constat 12) : sur une page 404, jamais le menu d'un niveau mémorisé.
  const [pageInconnue, setPageInconnue] = useState(false)
  const niveau = pageInconnue ? (workspaceIdActif ? 'projet' : 'organisation') : niveauDeLaRoute

  const changerModeNavigation = (mode: NavigationMode): void => setNavigationMode(mode)

  // U6b vague 9 : téléphone (tiroir + feuille de contexte), tablette (rail + panneau), bureau.
  const variante = useVarianteCoquille()
  const [contexteOuvert, setContexteOuvert] = useState(false)
  const fermerContexte = useCallback(() => setContexteOuvert(false), [])
  const [groupeRail, setGroupeRail] = useState<string | undefined>(undefined)

  // U6b vague 10 : le thème est mémorisé PAR UTILISATEUR ; défaut = préférence du système.
  useEffect(() => { applyTheme(getTheme(session.username || undefined)) }, [session.username])

  // Raccourci ⌘K / Ctrl-K : ouvrir la palette de commandes de n'importe où.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      // Après un changement de langue ou une authentification, le navigateur peut rendre
      // le focus au document. Dans cet état, le premier Tab doit toujours commencer par
      // le lien d'évitement, avant le chrome de navigation.
      if (e.key === 'Tab' && !e.shiftKey && document.activeElement === document.body) {
        e.preventDefault()
        skipLinkRef.current?.focus()
        return
      }
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setCommandOpen((open) => !open)
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  return (
    // `--rail-width` : le panneau shadcn est en position fixed et se place à cette distance
    // du bord. La navigation cible v0.5.0 (B-601) n'a plus de rail de domaines : les neuf
    // sections sont le premier niveau du panneau lui-même, la variable vaut donc 0.
    <RegionVivanteProvider>
    <ConfirmationProvider>
      {/* Lien d'évitement : premier élément focusable du document, hors du
          wrapper de la sidebar qui masque son contenu hors cadre. */}
      <a ref={skipLinkRef} href="#main" className="skip-link">{t('shell.skipToContent', 'Aller au contenu principal')}</a>
      <SidebarProvider style={{ '--rail-width': '0px' } as React.CSSProperties}
                       className={variante === 'bureau' ? 'coquille-bureau' : undefined}>
      <AppSidebar session={sessionPourNav} mode={navigationMode} niveau={niveau}
                  onModeChange={changerModeNavigation}
                  groupeADeplier={variante === 'tablette' ? groupeRail : undefined}
                  onRechercher={() => setCommandOpen(true)} onNewProject={openNewProject}
                  slotPied={workspaceIdActif
                    ? <QuotaBadgeSlotPied workspaceId={workspaceIdActif} />
                    : undefined} />

      {variante === 'tablette' ? (
        <RailTablette roles={sessionPourNav.roles} mode={navigationMode} niveau={niveau}
                      groupeOuvert={groupeRail} onGroupe={setGroupeRail} />
      ) : null}

      <SidebarInset className="min-w-0">
        <TopBar session={sessionPourNav} onOpenCommand={() => setCommandOpen(true)}
                contexteOuvert={contexteOuvert} onOuvrirContexte={() => setContexteOuvert(true)}
                onNewProject={openNewProject} navigationMode={navigationMode}
                onNavigationModeChange={changerModeNavigation} navigationModeError={navigationModeError}
                navigationModeConflict={navigationModeConflict}
                onConfirmNavigationModeFreshValue={confirmNavigationModeFreshValue} />
        {/* U6b vague 1 : le sélecteur de contexte est PERMANENT (plus de drapeau `navContexte`). */}
        {/* Téléphone : la même barre, dans une feuille plein écran ouverte par la pastille. */}
        {/* Bureau : les sélecteurs sont DANS la barre haute (planche BarreHaute) — pas de 2e rangée. */}
        {variante === 'bureau' ? null : (
          <FeuilleContexte telephone={variante === 'telephone'} ouvert={contexteOuvert} onFermer={fermerContexte}>
            <BarreContexte session={sessionPourNav} />
          </FeuilleContexte>
        )}
        {/* Bandeau d'arrêt d'urgence (§ 4.10.1) : sur toutes les routes, lu avec les rôles BRUTS
            du jeton — ce sont eux que la passerelle applique aux points de lecture. */}
        <BandeauArretIA roles={session.roles} organisation={session.organisationId} />
        {/* Lot PL (SRF-PLT-009) : accès exceptionnel de la plateforme en cours, visible de tous les membres. */}
        <BandeauAccesExceptionnel organisation={session.organisationId} />
        {/* Lot CYC (V650) : suppression programmée de l'organisation ou du projet actif, visible de tous. */}
        <BandeauCycleDeVie organisation={session.organisationId} projetActif={workspaceIdActif}
                           administration={estAdministrationOrganisation(session)} />
        <WorkflowRail />
        {/* `min-w-0` est indispensable, pas décoratif : un élément flex a `min-width: auto`
            par défaut, donc il refuse de rétrécir sous la largeur de son contenu. Les
            `overflow-x-auto` posés sur les tableaux et sur le kanban restaient alors sans
            effet, et c'est la PAGE ENTIÈRE qui se décalait latéralement — 954 px pour un
            viewport de 393 px sur le tableau de bord (UX-D0011). */}
        <main id="main" tabIndex={-1} className="min-w-0 flex-1 p-4 focus:outline-none sm:p-6">
          {/* Téléphone (CoquilleTelephone) : retour « ‹ Entrée » et pastille N28 en tête du contenu. */}
          <BandeTelephone />
          {/* Bureau (bloc_titre du canevas) : fil d'Ariane au-dessus du h1, prochaine action à droite. */}
          {variante === 'bureau' ? (
            <div data-coquille="entete-page" className="-mt-2 mb-1 flex min-h-11 min-w-0 items-center gap-3">
              <FilAriane partie="large" />
              <ProchaineActionProjet />
            </div>
          ) : null}
          <AvisDemenagement />
          {/* Lot NAV 0.5.x (registre n° 11) : projet dont on n'est pas membre, ou archivé — un écran
              clair au lieu d'une cascade de refus. */}
          <FournisseurPageInconnue signaler={setPageInconnue}>
            <PorteProjet session={session} niveau={niveau}>{children}</PorteProjet>
          </FournisseurPageInconnue>
        </main>
      </SidebarInset>

      {/* B-604 : le registre de vocabulaire par défaut suit la vue par profil. */}
      <RegistreSelonProfil />
      <CommandPalette open={commandOpen} onOpenChange={setCommandOpen} session={sessionPourNav}
                      onNewProject={openNewProject} />
      <NewWorkspaceDialog open={newProjectOpen} onOpenChange={setNewProjectOpen} />
      <Toaster />
    </SidebarProvider>
    </ConfirmationProvider>
    </RegionVivanteProvider>
  )
}
